import React, { useState, useEffect, useMemo } from 'react'
import { Search, Plus } from 'lucide-react'
import { ColDef } from 'ag-grid-community'
import Button from '@/components/ui/Button'
import DataGrid from '@/components/data/DataGrid'
import { designTokens } from '@/utils/design-tokens'
import { ArticleService } from '@/api/generated/services/ArticleService'
import type { ArticleResponse } from '@/api/generated/models/ArticleResponse'
import { useAppSelector } from '@/store/hooks'
import { useAlert } from '@/components/ui/Alert/exports'
import MeetingCreatePanel from './components/MeetingCreatePanel'
import MeetingDetailPanel from './components/MeetingDetailPanel'
import MeetingEditPanel from './components/MeetingEditPanel'

export default function MeetingManagementPage() {
  const { showError } = useAlert()
  const [searchValue, setSearchValue] = useState('')
  const [meetingNotes, setMeetingNotes] = useState<ArticleResponse[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false)
  const [isDetailPanelOpen, setIsDetailPanelOpen] = useState(false)
  // selectedMeeting은 EditPanel이 form 초기값으로 필요. DetailPanel은 id만 사용해 자체 fetch.
  const [selectedMeeting, setSelectedMeeting] = useState<ArticleResponse | null>(null)
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false)

  // Redux state
  const selectedWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)

  // Load meeting notes when component mounts or workspace changes
  useEffect(() => {
    if (selectedWorkspace?.id) {
      loadMeetingNotes()
    }
  }, [selectedWorkspace?.id])

  const loadMeetingNotes = async () => {
    if (!selectedWorkspace?.id) return

    setIsLoading(true)

    try {
      const response = await ArticleService.appsArticleViewsListArticles({
        workspaceId: selectedWorkspace.id,
        q: searchValue || undefined,
        page: 1,
        perPage: 100
      })

      if (response.data) {
        setMeetingNotes(response.data as ArticleResponse[])
      }
    } catch (err) {
      console.error('Failed to load meeting notes:', err)
      showError('회의록 목록을 불러오는 중 오류가 발생했습니다.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSearch = () => {
    loadMeetingNotes()
  }

  const handleCreateMeeting = () => {
    setIsCreatePanelOpen(true)
  }

  const handleCreatePanelClose = () => {
    setIsCreatePanelOpen(false)
  }

  const handleCreateSuccess = () => {
    loadMeetingNotes()
  }

  const handleDetailPanelClose = () => {
    setIsDetailPanelOpen(false)
    setSelectedMeeting(null)
  }

  const handleDetailSuccess = () => {
    loadMeetingNotes()
  }

  const handleEditPanelOpen = (meeting: ArticleResponse) => {
    setSelectedMeeting(meeting)
    setIsDetailPanelOpen(false)
    setIsEditPanelOpen(true)
  }

  const handleEditPanelClose = () => {
    setIsEditPanelOpen(false)
    setSelectedMeeting(null)
  }

  const handleEditSuccess = () => {
    loadMeetingNotes()
    setIsEditPanelOpen(false)
    setSelectedMeeting(null)
  }

  const handleRowClick = (params: any) => {
    const meeting = params.data as ArticleResponse
    setSelectedMeeting(meeting)
    setIsDetailPanelOpen(true)
  }

  // AG-Grid column definitions
  const columnDefs: ColDef[] = useMemo(() => [
    {
      headerName: '제목',
      field: 'title',
      flex: 2,
      minWidth: 200,
      cellRenderer: (params: any) => (
        <div className="flex items-center h-full">
          <span className="truncate">{params.value || '-'}</span>
        </div>
      )
    },
    {
      headerName: '담당자',
      field: 'assignee_name',
      width: 120,
      cellRenderer: (params: any) => (
        <div className="flex items-center h-full">
          <span>{params.value || '-'}</span>
        </div>
      )
    },
    {
      headerName: '생성자',
      field: 'creator_name',
      width: 120,
      cellRenderer: (params: any) => (
        <div className="flex items-center h-full">
          <span>{params.value || '-'}</span>
        </div>
      )
    },
    {
      headerName: '생성일시',
      field: 'created_at',
      width: 180,
      cellRenderer: (params: any) => (
        <div className="flex items-center h-full">
          <span>
            {params.value ? new Date(params.value).toLocaleString('ko-KR', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit'
            }) : '-'}
          </span>
        </div>
      )
    },
    {
      headerName: '최종수정일시',
      field: 'updated_at',
      width: 180,
      cellRenderer: (params: any) => (
        <div className="flex items-center h-full">
          <span>
            {params.value ? new Date(params.value).toLocaleString('ko-KR', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit'
            }) : '-'}
          </span>
        </div>
      )
    }
  ], [])

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Filter & Action Bar - Single Row */}
      <div
        className="py-2 px-4 flex-shrink-0 flex items-center gap-2 flex-wrap justify-end rounded-lg"
        style={{
          backgroundColor: designTokens.colors.gray[0]
        }}
      >
        {/* Search Input */}
        <div className="relative w-[280px] flex-shrink-0">
          <div
            className="flex items-center gap-1.5 px-3 py-0 h-10 border-2 rounded transition-all duration-150"
            style={{
              backgroundColor: designTokens.colors.white,
              borderColor: designTokens.colors.gray[1]
            }}
          >
            <Search
              className="w-5 h-5"
              style={{ color: designTokens.colors.gray[3] }}
            />
            <input
              type="text"
              placeholder="회의록을 검색해주세요."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="flex-1 bg-transparent outline-none min-w-0"
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: designTokens.typography.fontSize['body-medium'][0],
                lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                color: searchValue ? designTokens.colors.gray[6] : designTokens.colors.gray[3]
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch()
                }
              }}
            />
          </div>
        </div>

        {/* Search Button */}
        <Button
          color="dark"
          size="medium"
          onClick={handleSearch}
          className="h-10 px-[10px] flex-shrink-0 whitespace-nowrap"
        >
          검색
        </Button>

        {/* Create Meeting Button */}
        <Button
          color="dark"
          size="medium"
          onClick={handleCreateMeeting}
          className="h-10 px-[10px] flex items-center gap-2 flex-shrink-0 whitespace-nowrap"
        >
          <Plus className="w-4 h-4" strokeWidth={2} />
          회의록 생성
        </Button>
      </div>


      {/* Content Area */}
      <div className="flex-1 min-h-0 mt-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-full">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <p className="text-gray-500">회의록 목록을 불러오는 중...</p>
            </div>
          ) : meetingNotes.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <p className="text-gray-600">등록된 회의록이 없습니다.</p>
                <p className="text-gray-500 mt-2">새로운 회의록을 생성해보세요.</p>
              </div>
            </div>
          ) : (
            <DataGrid
              columnDefs={columnDefs}
              rowData={meetingNotes}
              headerHeight={40}
              rowHeight={42}
              pagination={true}
              paginationPageSize={20}
              paginationPageSizeSelector={[10, 20, 50, 100]}
              height="calc(100vh - 210px)"
              rowSelection="single"
              onRowClicked={handleRowClick}
            />
          )}
        </div>
      </div>

      {/* Meeting Create Panel */}
      <MeetingCreatePanel
        isOpen={isCreatePanelOpen}
        onClose={handleCreatePanelClose}
        onSuccess={handleCreateSuccess}
      />

      {/* Meeting Detail Panel — id만 전달, 패널이 자체 fetch */}
      <MeetingDetailPanel
        isOpen={isDetailPanelOpen}
        onClose={handleDetailPanelClose}
        meetingId={selectedMeeting?.id ?? null}
        onSuccess={handleDetailSuccess}
        onEdit={handleEditPanelOpen}
      />

      {/* Meeting Edit Panel */}
      <MeetingEditPanel
        isOpen={isEditPanelOpen}
        onClose={handleEditPanelClose}
        selectedMeeting={selectedMeeting}
        onSuccess={handleEditSuccess}
      />
    </div>
  )
}